import ResponsiveTable from "@/components/ResponsiveTable";
import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ROLE_LABELS } from "@/lib/constants";
import { toast } from "sonner";
import { Plus, KeyRound, UserCheck, UserX, Building2, Users, Wallet, Plug } from "lucide-react";
import PasswordForm from "@/components/PasswordForm";
import DeactivateUserForm from "@/components/DeactivateUserForm";
import ImageUploadField from "@/components/ImageUploadField";
import OperationsSettings from "@/components/OperationsSettings";
import "./Settings.css";

const categories = [["company", "Firma Profili", Building2], ["account", "Hesap Ayarları", KeyRound], ["users", "Kullanıcılar", Users], ["payments", "Ödeme Seçenekleri", Wallet], ["integrations", "Entegrasyonlar", Plug]];

export default function Settings() {
  const { user, company, setCompany } = useAuth();
  const [comp, setComp] = useState(company || {});
  const [category, setCategory] = useState("company");
  const [users, setUsers] = useState([]);
  const [passwordTarget, setPasswordTarget] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [userStatus, setUserStatus] = useState("active");
  const [accountBusy, setAccountBusy] = useState(false);
  const [showUserForm, setShowUserForm] = useState(false);
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "", role: "agent", phone: "" });

  useEffect(() => { setComp(company || {}); }, [company]);
  useEffect(() => {
    if (user?.role === "admin") api.get("/company/users").then(({ data }) => setUsers(data)).catch(e => toast.error(formatApiError(e)));
  }, [user?.role]);

  if (user?.role !== "admin") {
    return <div className="space-y-6"><h1 className="text-3xl font-bold">Ayarlar</h1><PasswordForm /></div>;
  }

  const saveCompany = async (e) => {
    e.preventDefault();
    try {
      await api.put("/company/me", {
        name: comp.name, short_name: comp.short_name,
        logo_url: comp.logo_url, primary_color: comp.primary_color, secondary_color: comp.secondary_color,
        phone: comp.phone, email: comp.email, website: comp.website, address: comp.address,
        presentation_title: comp.presentation_title, presentation_footer: comp.presentation_footer,
      });
      toast.success("Firma bilgileri güncellendi");
      const { data } = await api.get("/company/me");
      setComp(data); setCompany(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const createUser = async (e) => {
    e.preventDefault();
    try {
      await api.post("/company/users", newUser);
      toast.success("Kullanıcı oluşturuldu");
      setShowUserForm(false);
      setNewUser({ name: "", email: "", password: "", role: "agent", phone: "" });
      const { data } = await api.get("/company/users");
      setUsers(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleUser = async (account) => {
    if (account.active !== false) { setPasswordTarget(null); setDeactivateTarget(account); return; }
    if (accountBusy || !window.confirm(`${account.name} hesabı ${account.active === false ? "aktif" : "pasif"} yapılsın mı?`)) return;
    setAccountBusy(true);
    try {
      await api.put(`/company/users/${account.id}`, { active: account.active === false });
      const { data } = await api.get("/company/users"); setUsers(data);
      toast.success("Hesap durumu güncellendi");
    } catch (e) { toast.error(formatApiError(e)); } finally { setAccountBusy(false); }
  };

  return (
    <div className="settings-page space-y-5" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 font-display">Ayarlar</h1>
        <p className="text-slate-500 mt-1">{company?.name}</p>
      </div>
      <div className="settings-categories" role="tablist" aria-label="Ayar kategorileri">
        {categories.map(([id, label, Icon], index) => <button key={id} type="button" role="tab" id={`settings-tab-${id}`} aria-selected={category === id} aria-controls={`settings-panel-${id}`} tabIndex={category === id ? 0 : -1} onClick={() => setCategory(id)} onKeyDown={e => {
          const target = e.key === "Home" ? 0 : e.key === "End" ? categories.length - 1 : e.key === "ArrowRight" ? (index + 1) % categories.length : e.key === "ArrowLeft" ? (index + categories.length - 1) % categories.length : null;
          if (target !== null) { e.preventDefault(); setCategory(categories[target][0]); document.getElementById(`settings-tab-${categories[target][0]}`)?.focus(); }
        }}><Icon size={18} aria-hidden="true" /><span>{label}</span></button>)}
      </div>
      <section role="tabpanel" id="settings-panel-account" aria-labelledby="settings-tab-account" hidden={category !== "account"}><PasswordForm /></section>
      <section role="tabpanel" id="settings-panel-payments" aria-labelledby="settings-tab-payments" hidden={category !== "payments"}><OperationsSettings section="payments" /></section>
      <section role="tabpanel" id="settings-panel-integrations" aria-labelledby="settings-tab-integrations" hidden={category !== "integrations"}><OperationsSettings section="integrations" /></section>

      <section role="tabpanel" id="settings-panel-company" aria-labelledby="settings-tab-company" hidden={category !== "company"}>
      <form onSubmit={saveCompany} className="space-y-4" data-testid="company-form">
        <div className="text-base font-semibold text-slate-900">Firma Profili</div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <F l="Firma Adı"><input className="input" value={comp.name || ""} onChange={(e) => setComp({ ...comp, name: e.target.value })} data-testid="company-name" /></F>
          <F l="Kısa Ad"><input className="input" value={comp.short_name || ""} onChange={(e) => setComp({ ...comp, short_name: e.target.value })} /></F>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">Firma Logosu</div>
            <ImageUploadField value={comp.logo_url} onChange={(url) => setComp((current) => ({ ...current, logo_url: url }))} />
          </div>
          <F l="Web Sitesi"><input className="input" value={comp.website || ""} onChange={(e) => setComp({ ...comp, website: e.target.value })} /></F>
          <F l="Telefon"><input className="input" value={comp.phone || ""} onChange={(e) => setComp({ ...comp, phone: e.target.value })} /></F>
          <F l="E-posta"><input type="email" className="input" value={comp.email || ""} onChange={(e) => setComp({ ...comp, email: e.target.value })} /></F>
          <F l="Adres"><input className="input" value={comp.address || ""} onChange={(e) => setComp({ ...comp, address: e.target.value })} /></F>
          <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <F l="Kurumsal Renk">
              <div className="flex items-center gap-2">
                <input type="color" value={comp.primary_color || "#0F172A"} onChange={(e) => setComp({ ...comp, primary_color: e.target.value })}
                       className="h-10 w-12 rounded border border-slate-200" data-testid="company-primary-color" />
                <input className="input" value={comp.primary_color || ""} onChange={(e) => setComp({ ...comp, primary_color: e.target.value })} />
              </div>
            </F>
            <F l="İkincil Renk">
              <div className="flex items-center gap-2">
                <input type="color" value={comp.secondary_color || "#F59E0B"} onChange={(e) => setComp({ ...comp, secondary_color: e.target.value })}
                       className="h-10 w-12 rounded border border-slate-200" />
                <input className="input" value={comp.secondary_color || ""} onChange={(e) => setComp({ ...comp, secondary_color: e.target.value })} />
              </div>
            </F>
          </div>
          <div className="md:col-span-2">
            <F l="Sunum Başlığı"><input className="input" value={comp.presentation_title || ""} onChange={(e) => setComp({ ...comp, presentation_title: e.target.value })} /></F>
          </div>
          <div className="md:col-span-2">
            <F l="Sunum Footer"><input className="input" value={comp.presentation_footer || ""} onChange={(e) => setComp({ ...comp, presentation_footer: e.target.value })} /></F>
          </div>
        </div>
        <div className="flex justify-end">
          <button type="submit" className="px-4 py-2 text-sm bg-slate-900 text-white rounded-lg" data-testid="save-company">Kaydet</button>
        </div>
      </form>
      </section>

      <section role="tabpanel" id="settings-panel-users" aria-labelledby="settings-tab-users" hidden={category !== "users"}>
      <div className="operations space-y-4" data-testid="users-section">
        <div className="flex flex-wrap gap-3 items-center justify-between">
          <div className="text-base font-semibold text-slate-900">Kullanıcılar ({users.length})</div>
          <button onClick={() => setShowUserForm(true)} className="bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 text-sm rounded-lg inline-flex items-center gap-1" data-testid="new-user-btn">
            <Plus className="w-4 h-4" /> Yeni Kullanıcı
          </button>
        </div>

        <div className="op-tabs" aria-label="Kullanıcı durumu">
          {[["active", "Aktif"], ["inactive", "Pasif"]].map(([value, label]) => <button key={value} type="button" aria-pressed={userStatus === value} onClick={() => { setUserStatus(value); setDeactivateTarget(null); setPasswordTarget(null); }}>{label} ({users.filter(u => (u.active !== false) === (value === "active")).length})</button>)}
        </div>
        {deactivateTarget && <DeactivateUserForm key={deactivateTarget.id} account={deactivateTarget} users={users} onDone={async saved => {
          setDeactivateTarget(null);
          if (saved) { toast.success("İş devri tamamlandı; hesap pasife alındı"); try { const { data } = await api.get("/company/users"); setUsers(data); } catch (e) { toast.error(formatApiError(e)); } }
        }} />}

        {showUserForm && (
          <form onSubmit={createUser} className="border border-slate-200 rounded-lg p-4 space-y-3 bg-slate-50" data-testid="user-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <F l="Ad Soyad"><input required className="input" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} /></F>
              <F l="E-posta"><input required type="email" className="input" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} /></F>
              <F l="Şifre"><input required minLength={8} maxLength={72} autoComplete="new-password" type="password" className="input" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} /></F>
              <F l="Rol">
                <select className="input" value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}>
                  {["admin", "manager", "agent", "demo_viewer"].map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                </select>
              </F>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setShowUserForm(false)} className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg">Vazgeç</button>
              <button type="submit" className="px-3 py-1.5 text-sm bg-slate-900 text-white rounded-lg" data-testid="submit-user">Oluştur</button>
            </div>
          </form>
        )}

        {passwordTarget && <PasswordForm key={passwordTarget.id} target={passwordTarget} onDone={saved => { setPasswordTarget(null); if (saved !== false) toast.success("Şifre değiştirildi; eski oturumlar geçersiz kılındı"); }} />}
        <div className="overflow-x-auto">
        <ResponsiveTable className="w-full min-w-[560px] text-sm">
          <thead className="text-xs uppercase tracking-wider text-slate-500">
            <tr><th className="text-left py-2">Ad</th><th className="text-left py-2">E-posta</th><th className="text-left py-2">Rol</th><th>Durum</th><th>İşlem</th></tr>
          </thead>
          <tbody>
            {!users.some(u => (u.active !== false) === (userStatus === "active")) && <tr><td colSpan={5} className="py-4 text-slate-500">{userStatus === "active" ? "Aktif kullanıcı bulunmuyor." : "Pasif kullanıcı bulunmuyor."}</td></tr>}
            {users.filter(u => (u.active !== false) === (userStatus === "active")).map((u) => (
              <tr key={u.id} className="border-t border-slate-100">
                <td className="py-2">{u.name}</td>
                <td className="py-2 text-slate-600">{u.email}</td>
                <td className="py-2">{ROLE_LABELS[u.role] || u.role}</td>
                <td className="py-2">{u.active === false ? "Pasif" : "Aktif"}</td>
                <td className="py-2 text-right">
                  {u.id !== user.id && (
                    <div className="flex flex-wrap gap-2 justify-end">
                      <button disabled={accountBusy} onClick={() => setPasswordTarget(u)} className="btn-secondary" title={`${u.name} şifresini değiştir`} aria-label={`${u.name} şifresini değiştir`}><KeyRound size={16} /></button>
                      <button disabled={accountBusy} onClick={() => toggleUser(u)} className="btn-secondary" title={u.active === false ? "Aktife al" : "Pasife al"} aria-label={`${u.name} ${u.active === false ? "aktife al" : "pasife al"}`}>{u.active === false ? <UserCheck size={16} /> : <UserX size={16} />}</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </ResponsiveTable>
        </div>
      </div>
      </section>
    </div>
  );
}

function F({ l, children }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">{l}</div>
      {children}
    </label>
  );
}
